// crm-flow.component.ts
import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import * as _ from 'lodash';
import { MatPaginator, MatSort, MatTableDataSource, MatDialog } from '@angular/material';

/**Service file */
import { SideNavService } from '../../../sidenav.service';
import { CommonOrdersService } from '../../../../commonOrders.service';

/** Local file */
import { displayValues, toastMsg } from '../../../../../../utils/index';
import { ScheduleQueryService } from '../../../../../../queryFiles';

// Import the dialog component
import { CustomerDetailsDialogComponent } from './customer-details-dialog/customer-details-dialog.component';



@Component({
  selector: 'app-crm-flow',
  templateUrl: './crm-flow.component.html',
  styleUrls: ['./crm-flow.component.scss']
})
export class CrmFlowComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('searchInput') searchInput: ElementRef;

  displayedColumns = [
    'customerName',
    'email',
    'phone',
    'address',
    'marketName',
    'firmName',
    'quotesOrdered',
    'repairsSubmitted',
    'moreDetails'
  ];

  displayValues = displayValues;
  toastMsg = toastMsg;

  // Subscriptions
  onCustomersChangedSubscription: Subscription;
  ordersSubscription: Subscription;
  scheduleRequestsSubscription: Subscription;

  // Loading states
  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';
  loadingMessage: string = 'Loading Customer Data';

  // Table data
  customersSource: MatTableDataSource<CustomerData>;
  @ViewChild(MatPaginator) customersPaginator: MatPaginator;
  @ViewChild(MatSort) customersSort: MatSort;

  selectedId: any;

  // Data arrays
  private allCustomers: any[] = [];
  private allRawOrders: any[] = [];
  private allScheduleRequests: any[] = [];
  private customerEmailMap: Map<string, any> = new Map();
  private customerStatsMap: Map<string, CustomerStats> = new Map();
  private orderIdToEmailMap: Map<string, string> = new Map();

  // Search functionality
  currentSearchTerm: string = '';
  isSearchMode: boolean = false;

  // Pagination info
  public customerCount: number = 0;
  public totalOrderCount: number = 0;
  public totalScheduleRequestCount: number = 0;
  public hasMoreOrderData: boolean = true;
  public orderEndCursor: string = null;
  public scheduleEndCursor: string = null;
  public hasMoreScheduleData: boolean = true;

  // Pagination settings
  currentPageIndex: number = 0;
  pageSize: number = 10;
  private ordersPerBatch: number = 100;
  private schedulePerBatch: number = 100;

  constructor(
    public sideNavService: SideNavService,
    public ecommerceOrderService: CommonOrdersService,
    public scheduleQueryService: ScheduleQueryService,
    public dialog: MatDialog
  ) { }

  ngOnInit() {
    this.initializeComponent();
  }

  ngAfterViewInit() {
    if (this.customersSource) {
      this.customersSource.paginator = this.customersPaginator;
      this.customersSource.sort = this.customersSort;
    }
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  private unsubscribeAll() {
    if (this.onCustomersChangedSubscription) {
      this.onCustomersChangedSubscription.unsubscribe();
    }
    if (this.ordersSubscription) {
      this.ordersSubscription.unsubscribe();
    }
    if (this.scheduleRequestsSubscription) {
      this.scheduleRequestsSubscription.unsubscribe();
    }
  }

  private initializeComponent() {
    this.resetComponentState();
    this.loadInitialData();
  }

private resetComponentState() {
  this.allCustomers = [];
  this.allRawOrders = [];
  this.allScheduleRequests = [];
  this.customerEmailMap.clear();
  this.customerStatsMap.clear();
  this.orderIdToEmailMap.clear();
  this.orderEndCursor = null;
  this.scheduleEndCursor = null; // Reset schedule cursor too
  this.hasMoreOrderData = true;
  this.hasMoreScheduleData = true;
  this.hasError = false;
  this.errorMessage = '';
  this.customerCount = 0;
  this.totalOrderCount = 0;
  this.totalScheduleRequestCount = 0;
  this.currentSearchTerm = '';
  this.isSearchMode = false;
}

private async loadInitialData() {
  this.isLoading = true;
  this.hasError = false;
  this.loadingMessage = 'Loading Customer Data';

  try {
    // Load first batch of orders
    this.loadingMessage = 'Loading Customers...';
    await this.loadOrdersBatch();
    
    // Load ALL schedule requests using the reliable method
    this.loadingMessage = 'Loading Customers...';
    await this.loadAllScheduleRequestsForSearch(); // Use same method as search
    
    // Process and display the data
    this.loadingMessage = 'Processing Data...';
    this.processAndDisplayData();
    
  } catch (error) {
    console.error('Error loading initial CRM data:', error);
    this.handleError(error);
  } finally {
    this.isLoading = false;
  }
}

private loadScheduleRequestsBatch(cursor: string = ''): Promise<{
  requests: any[],
  endCursor: string,
  hasMoreData: boolean,
  totalCount: number
}> {
  return new Promise((resolve, reject) => {
    const subscription = this.scheduleQueryService
      .GET_ALL_SCHEDULE_REQUESTSS(this.schedulePerBatch, cursor)
      .valueChanges.subscribe(
        (response: any) => {
          try {
            subscription.unsubscribe(); // Clean up this batch subscription
            
            if (response && response.data && response.data.allScheduleRequests) {
              const scheduleData = response.data.allScheduleRequests;
              
              if (scheduleData.edges) {
                // Handle cursor-based pagination response
                const requests = scheduleData.edges.map((edge: any) => edge.node) || [];
                const pageInfo = scheduleData.pageInfo;
                
                resolve({
                  requests: requests,
                  endCursor: pageInfo ? pageInfo.endCursor : '',
                  hasMoreData: pageInfo ? pageInfo.hasNextPage : false,
                  totalCount: scheduleData.totalCount || 0
                });
              } else if (scheduleData.nodes) {
                // Handle direct nodes response
                const requests = scheduleData.nodes || [];
                
                resolve({
                  requests: requests,
                  endCursor: '',
                  hasMoreData: requests.length === this.schedulePerBatch,
                  totalCount: scheduleData.totalCount || requests.length
                });
              } else {
                resolve({
                  requests: [],
                  endCursor: '',
                  hasMoreData: false,
                  totalCount: 0
                });
              }
            } else {
              resolve({
                requests: [],
                endCursor: '',
                hasMoreData: false,
                totalCount: 0
              });
            }
          } catch (error) {
            reject(error);
          }
        },
        (error) => {
          subscription.unsubscribe(); // Clean up on error
          reject(error);
        }
      );
  });
}

  // Search functionality using GET_ALL_ORDERS query
async performSearch(searchTerm: string) {
  if (!searchTerm || searchTerm.trim() === '') {
    this.clearSearch();
    return;
  }

  this.currentSearchTerm = searchTerm.trim();
  this.isSearchMode = true;
  this.isLoading = true;
  this.loadingMessage = `Searching for "${this.currentSearchTerm}" in names and emails...`;

  try {
    // Search orders using corrected query-based approach (firstName and clientemail only)
    console.log('Starting search for:', this.currentSearchTerm);
    await this.searchOrdersWithQuery(this.currentSearchTerm);
    
    // Reset schedule state before loading
    this.allScheduleRequests = [];
    this.scheduleEndCursor = null;
    this.hasMoreScheduleData = true;
    this.totalScheduleRequestCount = 0;
    
    // Load ALL schedule requests to match against search results
    this.loadingMessage = 'Loading all Customers...';
    console.log('Loading schedule requests after search...');
    await this.loadAllScheduleRequestsForSearch();
    
    // Process search results
    console.log('Processing search results...');
    this.processSearchResults();
    
  } catch (error) {
    console.error('Error performing search:', error);
    this.handleError(error);
  } finally {
    this.isLoading = false;
  }
}

private async loadAllScheduleRequestsForSearch(): Promise<void> {
  try {
    let hasMoreData = true;
    let cursor = '';
    let allRequests: any[] = [];
    let batchCount = 0;

    console.log('Starting to load all schedule requests for search...');

    while (hasMoreData) {
      try {
        batchCount++;
        this.loadingMessage = `Loading Customers...`;
        
        const batchData = await this.loadScheduleRequestsBatchDirect(cursor);
        
        if (batchData.requests && batchData.requests.length > 0) {
          allRequests = allRequests.concat(batchData.requests);
          cursor = batchData.endCursor;
          hasMoreData = batchData.hasMoreData;
          
          console.log(`Schedule requests batch ${batchCount} loaded:`, {
            batchSize: batchData.requests.length,
            totalLoaded: allRequests.length,
            hasMoreData: hasMoreData,
            nextCursor: cursor ? cursor.substring(0, 20) + '...' : 'none'
          });
          
          // Update loading message with progress
          // this.loadingMessage = `Loading Schedule Requests... (${allRequests.length} loaded)`;
          
        } else {
          console.log(`No more requests in batch ${batchCount}, stopping...`);
          hasMoreData = false;
        }

        // Safety check to prevent infinite loops
        if (batchCount > 50) {
          console.warn('Too many batches, stopping to prevent infinite loop');
          break;
        }
      } catch (batchError) {
        console.error(`Error loading schedule requests batch ${batchCount}:`, batchError);
        break; // Stop loading on error but don't fail completely
      }
    }

    // Update component state
    this.allScheduleRequests = allRequests;
    this.totalScheduleRequestCount = allRequests.length;
    this.hasMoreScheduleData = false;

    console.log('Schedule requests loading complete:', {
      totalLoaded: allRequests.length,
      totalCount: this.totalScheduleRequestCount,
      batchesProcessed: batchCount
    });

  } catch (error) {
    console.error('Error in loadAllScheduleRequestsForSearch:', error);
    // Continue with empty schedule requests rather than failing
    this.allScheduleRequests = [];
    this.totalScheduleRequestCount = 0;
  }
}

private loadScheduleRequestsBatchDirect(cursor: string = ''): Promise<{
  requests: any[],
  endCursor: string,
  hasMoreData: boolean,
  totalCount: number
}> {
  return new Promise((resolve, reject) => {
    console.log(`Loading schedule batch with cursor: ${cursor ? cursor.substring(0, 20) + '...' : 'initial'}`);
    
    let subscription: any;
    
    // Set timeout to prevent hanging
    const timeout = setTimeout(() => {
      if (subscription) {
        subscription.unsubscribe();
      }
      reject(new Error('Schedule requests batch loading timeout'));
    }, 30000); // 30 second timeout

    try {
      subscription = this.scheduleQueryService
        .GET_ALL_SCHEDULE_REQUESTSS(this.schedulePerBatch, cursor)
        .valueChanges.subscribe(
          (response: any) => {
            clearTimeout(timeout);
            subscription.unsubscribe();
            
            try {
              // console.log('Schedule batch response received:', {
              //   hasData: !!response?.data,
              //   hasScheduleRequests: !!response?.data?.allScheduleRequests
              // });

              if (response && response.data && response.data.allScheduleRequests) {
                const scheduleData = response.data.allScheduleRequests;
                
                if (scheduleData.edges && Array.isArray(scheduleData.edges)) {
                  // Handle cursor-based pagination response
                  const requests = scheduleData.edges.map((edge: any) => edge.node).filter(node => node);
                  const pageInfo = scheduleData.pageInfo || {};
                  
                  console.log('Processing cursor-based response:', {
                    edgesCount: scheduleData.edges.length,
                    requestsCount: requests.length,
                    hasNextPage: pageInfo.hasNextPage,
                    endCursor: pageInfo.endCursor ? pageInfo.endCursor.substring(0, 20) + '...' : 'none'
                  });
                  
                  resolve({
                    requests: requests,
                    endCursor: pageInfo.endCursor || '',
                    hasMoreData: pageInfo.hasNextPage || false,
                    totalCount: scheduleData.totalCount || 0
                  });
                  
                } else if (scheduleData.nodes && Array.isArray(scheduleData.nodes)) {
                  // Handle direct nodes response
                  const requests = scheduleData.nodes.filter(node => node);
                  
                  console.log('Processing nodes-based response:', {
                    nodesCount: scheduleData.nodes.length,
                    requestsCount: requests.length
                  });
                  
                  resolve({
                    requests: requests,
                    endCursor: '',
                    hasMoreData: requests.length === this.schedulePerBatch,
                    totalCount: scheduleData.totalCount || requests.length
                  });
                  
                } else {
                  console.log('No edges or nodes found in response');
                  resolve({
                    requests: [],
                    endCursor: '',
                    hasMoreData: false,
                    totalCount: 0
                  });
                }
              } else {
                console.log('No schedule data in response');
                resolve({
                  requests: [],
                  endCursor: '',
                  hasMoreData: false,
                  totalCount: 0
                });
              }
            } catch (parseError) {
              console.error('Error parsing schedule response:', parseError);
              reject(parseError);
            }
          },
          (error) => {
            clearTimeout(timeout);
            subscription.unsubscribe();
            console.error('GraphQL error loading schedule requests:', error);
            reject(error);
          }
        );
    } catch (error) {
      clearTimeout(timeout);
      console.error('Error setting up schedule requests subscription:', error);
      reject(error);
    }
  });
}

private searchOrdersWithQuery(searchTerm: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    // Unsubscribe from previous subscription
    if (this.ordersSubscription) {
      this.ordersSubscription.unsubscribe();
    }

    console.log('Executing search query for:', searchTerm);

    // Use the corrected search method (firstName and clientemail only)
    const searchPromise = this.ecommerceOrderService
      .getAllOrderIdWithSearchStrSpecificFields(searchTerm, true);

    // Handle the search promise
    searchPromise
      .then((searchResults: any[]) => {
        this.allRawOrders = searchResults || [];
        this.totalOrderCount = this.allRawOrders.length;
        this.hasMoreOrderData = false;
        
        console.log('Search results loaded:', {
          searchTerm: searchTerm,
          ordersFound: this.allRawOrders.length,
          searchFields: ['firstName', 'clientemail']
        });
        
        // Log sample of found customers for debugging
        if (this.allRawOrders.length > 0) {
          const sampleOrders = this.allRawOrders.slice(0, 3).map(order => ({
            id: order.id,
            firstName: order.firstName,
            clientemail: order.clientemail
          }));
          console.log('Sample search results:', sampleOrders);
        }
        
        resolve();
      })
      .catch((error) => {
        console.error('Error searching orders:', error);
        reject(error);
      });

    // Create a dummy subscription for cleanup
    this.ordersSubscription = new Subscription();
    this.ordersSubscription.add(() => {
      // Cleanup logic if needed when unsubscribing
    });
  });
}

  private processSearchResults() {
    // Process customers from search results
    this.processCustomersData();
    
    // Build order-to-email mapping for repairs
    this.buildOrderToEmailMapping();
    
    // Update statistics
    this.updateCustomersWithStatistics();
  }

  clearSearch() {
    this.currentSearchTerm = '';
    this.isSearchMode = false;
    this.refreshCustomers();
  }

  private async loadOrdersBatch(cursor: string = null): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Unsubscribe from previous subscription
      if (this.ordersSubscription) {
        this.ordersSubscription.unsubscribe();
      }

      const queryCursor = cursor || '';
      this.ordersSubscription = this.ecommerceOrderService.commonQueryService
        .GET_ALL_ORDERS(this.ordersPerBatch, queryCursor)
        .valueChanges.subscribe(
          (response: any) => {
            try {
              this.handleOrdersResponse(response, cursor === null);
              resolve();
            } catch (error) {
              reject(error);
            }
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

private async loadScheduleRequests(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    // Unsubscribe from previous subscription
    if (this.scheduleRequestsSubscription) {
      this.scheduleRequestsSubscription.unsubscribe();
    }

    // Load all schedule requests in batches
    this.loadAllScheduleRequests()
      .then(() => {
        console.log('All schedule requests loaded:', {
          totalLoaded: this.allScheduleRequests.length,
          totalCount: this.totalScheduleRequestCount
        });
        resolve();
      })
      .catch((error) => {
        console.warn('Error loading schedule requests:', error);
        resolve(); // Don't fail the entire process
      });
  });
}

private async loadAllScheduleRequests(): Promise<void> {
  let hasMoreData = true;
  let cursor = this.scheduleEndCursor || '';
  let allRequests: any[] = [];

  while (hasMoreData) {
    try {
      const batchData = await this.loadScheduleRequestsBatch(cursor);
      
      if (batchData.requests && batchData.requests.length > 0) {
        allRequests = allRequests.concat(batchData.requests);
        cursor = batchData.endCursor;
        hasMoreData = batchData.hasMoreData;
        
        // Update loading message with progress
        // this.loadingMessage = `Loading Schedule Requests... (${allRequests.length}/${batchData.totalCount || 'unknown'})`;
        
        console.log('Schedule requests batch loaded:', {
          batchSize: batchData.requests.length,
          totalLoaded: allRequests.length,
          hasMoreData: hasMoreData,
          nextCursor: cursor
        });
      } else {
        hasMoreData = false;
      }
    } catch (error) {
      console.error('Error loading schedule requests batch:', error);
      break; // Stop loading on error but don't fail completely
    }
  }

  // Update component state with all loaded requests
  this.allScheduleRequests = allRequests;
  this.totalScheduleRequestCount = allRequests.length;
  this.hasMoreScheduleData = false;
  this.scheduleEndCursor = cursor;
}

  private handleOrdersResponse(response: any, isInitial: boolean) {
    if (response && response.data && response.data.allOrders) {
      const ordersData = response.data.allOrders;
      const newOrders = ordersData.nodes || [];
      const pageInfo = ordersData.pageInfo;

      // Update pagination info
      this.totalOrderCount = ordersData.totalCount || 0;
      this.hasMoreOrderData = pageInfo ? pageInfo.hasNextPage : false;
      this.orderEndCursor = pageInfo ? pageInfo.endCursor : null;

      // Add new orders to our collection
      if (isInitial) {
        this.allRawOrders = newOrders;
      } else {
        this.allRawOrders = this.allRawOrders.concat(newOrders);
      }

      console.log('Orders loaded:', {
        newOrdersCount: newOrders.length,
        totalOrdersLoaded: this.allRawOrders.length,
        totalOrderCount: this.totalOrderCount,
        hasMoreData: this.hasMoreOrderData
      });

    } else if (response && response.nodes) {
      // Handle direct nodes response format
      const newOrders = response.nodes || [];
      if (isInitial) {
        this.allRawOrders = newOrders;
      } else {
        this.allRawOrders = this.allRawOrders.concat(newOrders);
      }
      this.hasMoreOrderData = newOrders.length === this.ordersPerBatch;
    }
  }

  private handleScheduleRequestsResponse(response: any) {
    if (response && response.data && response.data.allScheduleRequests) {
      const scheduleData = response.data.allScheduleRequests;
      
      if (scheduleData.edges) {
        // Handle cursor-based pagination response
        const requests = scheduleData.edges.map((edge: any) => edge.node) || [];
        const pageInfo = scheduleData.pageInfo;
        
        this.allScheduleRequests = requests;
        this.totalScheduleRequestCount = scheduleData.totalCount || requests.length;
        this.hasMoreScheduleData = pageInfo ? pageInfo.hasNextPage : false;
        this.scheduleEndCursor = pageInfo ? pageInfo.endCursor : null;
        
        console.log('Schedule requests loaded (cursor-based):', {
          requestsCount: requests.length,
          totalCount: this.totalScheduleRequestCount,
          hasMoreData: this.hasMoreScheduleData
        });
      } else if (scheduleData.nodes) {
        // Handle direct nodes response
        const requests = scheduleData.nodes || [];
        
        this.allScheduleRequests = requests;
        this.totalScheduleRequestCount = scheduleData.totalCount || requests.length;
        this.hasMoreScheduleData = requests.length === this.schedulePerBatch;
        
        console.log('Schedule requests loaded (direct nodes):', {
          requestsCount: requests.length,
          totalCount: this.totalScheduleRequestCount
        });
      }
    }
  }

  private processAndDisplayData() {
    // Process customers from current orders
    this.processCustomersData();
    
    // Build order-to-email mapping for repairs
    this.buildOrderToEmailMapping();
    
    // Update statistics by matching orders and schedule requests
    this.updateCustomersWithStatistics();
  }

  private processCustomersData() {
    this.customerEmailMap.clear();

    this.allRawOrders.forEach(order => {
      const customer = this.parseCustomerFromOrder(order);
      if (customer) {
        const email = customer.email.toLowerCase();
        const existingCustomer = this.customerEmailMap.get(email);

        if (!existingCustomer || new Date(customer.createdAt) > new Date(existingCustomer.createdAt)) {
          this.customerEmailMap.set(email, customer);
        }
      }
    });

    this.allCustomers = Array.from(this.customerEmailMap.values());
    this.allCustomers = _.orderBy(this.allCustomers, 'createdAt', 'desc');
  }

  private buildOrderToEmailMapping() {
    this.orderIdToEmailMap.clear();
    
    this.allRawOrders.forEach(order => {
      const customer = this.parseCustomerFromOrder(order);
      if (customer && customer.email) {
        this.orderIdToEmailMap.set(order.id, customer.email.toLowerCase());
      }
    });
  }

private processCustomerStatistics() {
  this.customerStatsMap.clear();

  console.log('Processing customer statistics:', {
    ordersCount: this.allRawOrders.length,
    scheduleRequestsCount: this.allScheduleRequests.length,
    customersCount: this.allCustomers.length
  });

  // Process orders to count quotes per customer
  let orderProcessedCount = 0;
  this.allRawOrders.forEach(order => {
    const customer = this.parseCustomerFromOrder(order);
    if (customer && customer.email) {
      const email = customer.email.toLowerCase();
      const stats = this.customerStatsMap.get(email) || { quotesOrdered: 0, repairsSubmitted: 0 };
      stats.quotesOrdered++;
      this.customerStatsMap.set(email, stats);
      orderProcessedCount++;
    }
  });

  console.log(`Processed ${orderProcessedCount} orders for quotes`);

  // Process schedule requests to count repairs per customer
  let repairProcessedCount = 0;
  let repairMatchedByOrderId = 0;
  let repairMatchedByEmail = 0;

  this.allScheduleRequests.forEach(request => {
    let customerEmail = null;
    let matchedBy = '';
    
    // Try to match by orderId first (if the order is in our loaded dataset)
    if (request.orderId && this.orderIdToEmailMap.has(request.orderId)) {
      customerEmail = this.orderIdToEmailMap.get(request.orderId);
      matchedBy = 'orderId';
      repairMatchedByOrderId++;
    } 
    // Fallback to matching by email if orderId doesn't match loaded orders
    else if (request.contactEmail) {
      const email = request.contactEmail.toLowerCase();
      // Only count if we have this customer in our orders dataset
      if (this.customerEmailMap.has(email)) {
        customerEmail = email;
        matchedBy = 'email';
        repairMatchedByEmail++;
      }
    }
    
    if (customerEmail) {
      const stats = this.customerStatsMap.get(customerEmail) || { quotesOrdered: 0, repairsSubmitted: 0 };
      stats.repairsSubmitted++;
      this.customerStatsMap.set(customerEmail, stats);
      repairProcessedCount++;
    }
  });

  console.log('Repair processing summary:', {
    totalScheduleRequests: this.allScheduleRequests.length,
    repairsProcessed: repairProcessedCount,
    matchedByOrderId: repairMatchedByOrderId,
    matchedByEmail: repairMatchedByEmail,
    customersWithStats: this.customerStatsMap.size
  });

  // Log sample of customer stats for debugging
  const sampleStats = Array.from(this.customerStatsMap.entries()).slice(0, 3);
  console.log('Sample customer stats:', sampleStats);
}

private updateCustomersWithStatistics() {
  this.processCustomerStatistics();

  console.log('Updating customers with statistics...');
  let customersWithRepairs = 0;

  this.allCustomers = this.allCustomers.map(customer => {
    const email = customer.email.toLowerCase();
    const stats = this.customerStatsMap.get(email) || { quotesOrdered: 0, repairsSubmitted: 0 };

    if (stats.repairsSubmitted > 0) {
      customersWithRepairs++;
    }

    return Object.assign({}, customer, {
      quotesOrdered: stats.quotesOrdered,
      repairsSubmitted: stats.repairsSubmitted
    });
  });

  console.log(`Updated ${this.allCustomers.length} customers, ${customersWithRepairs} have repairs`);

  this.assignDataSource(this.allCustomers);
}

  private assignDataSource(customers: any[]) {
    this.customersSource = new MatTableDataSource(customers);
    if (this.customersPaginator) {
      this.customersSource.paginator = this.customersPaginator;
    }
    if (this.customersSort) {
      this.customersSource.sort = this.customersSort;
    }
    
    this.customerCount = customers.length;
    this.highLightFirstRow();
  }

  async onPaginateChange(event: any) {
    this.currentPageIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    // If in search mode, don't load more data
    if (this.isSearchMode) {
      return;
    }

    // Calculate if we need to load more data
    const requiredCustomers = (event.pageIndex + 1) * event.pageSize;
    const loadThreshold = 20; // Load more data when we're within 20 customers of the end

    if (requiredCustomers > this.allCustomers.length - loadThreshold && 
        this.hasMoreOrderData && 
        !this.isLoading) {
      
      await this.loadMoreData();
    }
  }

  private async loadMoreData() {
    if (!this.hasMoreOrderData || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.loadingMessage = 'Loading More Data...';

    try {
      // Load next batch of orders
      await this.loadOrdersBatch(this.orderEndCursor);
      
      // Load more schedule requests if available
      if (this.hasMoreScheduleData) {
        await this.loadScheduleRequests();
      }
      
      // Process new data
      this.processAndDisplayData();
      
    } catch (error) {
      console.error('Error loading more data:', error);
      this.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private parseCustomerFromOrder(order: any): CustomerData | null {
    if (!order.orderContent) return null;

    try {
      const orderContentObj = JSON.parse(order.orderContent);
      const billing = orderContentObj.billing;

      if (!billing || !billing.email) return null;

      const marketName = order.marketByMarketId && order.marketByMarketId.marketName
        ? this.decodeURIComponent(order.marketByMarketId.marketName.replace(/\+/g, ' '))
        : '-';

      const firmName = order.firmByFirmId && order.firmByFirmId.firmsName
        ? this.decodeURIComponent(order.firmByFirmId.firmsName.replace(/\+/g, ' '))
        : '-';

      return {
        orderId: order.id,
        customerName: this.getCustomerName(billing),
        email: billing.email,
        phone: billing.phone || '-',
        address: this.getCustomerAddress(billing),
        createdAt: order.createdAt,
        orderContent: orderContentObj,
        originalOrder: order,
        quotesOrdered: 0,
        repairsSubmitted: 0,
        marketName: marketName,
        firmName: firmName
      };
    } catch (e) {
      console.error('Error parsing orderContent for order:', order.id, e);
      return null;
    }
  }

  decodeURIComponent(value: string): string {
    if (!value) return value;
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private getCustomerName(billing: any): string {
    if (billing.first_name && billing.last_name) {
      return billing.first_name + ' ' + billing.last_name;
    } else if (billing.first_name) {
      return billing.first_name;
    } else if (billing.last_name) {
      return billing.last_name;
    }
    return '-';
  }

  private getCustomerAddress(billing: any): string {
    const addressParts = [];

    if (billing.address_1) addressParts.push(billing.address_1);
    if (billing.address_2) addressParts.push(billing.address_2);
    if (billing.city) addressParts.push(billing.city);
    if (billing.state) addressParts.push(billing.state);
    if (billing.postcode) addressParts.push(billing.postcode);
    if (billing.country) addressParts.push(billing.country);

    return addressParts.length > 0 ? addressParts.join(', ') : '-';
  }

  // UI event handlers
  onSelect(selected: any) {
    this.selectedId = selected.orderId;
  }

refreshCustomers() {
  // Clear the search input field
  if (this.searchInput) {
    this.searchInput.nativeElement.value = '';
  }
  
  // Clear search state
  this.currentSearchTerm = '';
  this.isSearchMode = false;
  
  // Reinitialize the component
  this.initializeComponent();
}

  // Customer Details Dialog functionality
  openCustomerDetailsDialog(customer: CustomerData) {
    const customerOrders = this.getCustomerOrders(customer.email);
    const customerRepairs = this.getCustomerRepairs(customer.email);

    const dialogRef = this.dialog.open(CustomerDetailsDialogComponent, {
      width: '95vw',
      maxWidth: '1400px',
      height: '90vh',
      maxHeight: '900px',
      data: {
        customer: customer,
        orders: customerOrders,
        repairs: customerRepairs,
        totalQuotes: customer.quotesOrdered,
        totalRepairs: customer.repairsSubmitted
      },
      panelClass: 'customer-details-dialog',
      disableClose: false,
      hasBackdrop: true
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Customer details dialog closed');
    });
  }

  private getCustomerOrders(customerEmail: string): any[] {
    return this.allRawOrders.filter(order => {
      try {
        const orderContent = JSON.parse(order.orderContent);
        return orderContent.billing &&
          orderContent.billing.email &&
          orderContent.billing.email.toLowerCase() === customerEmail.toLowerCase();
      } catch (e) {
        return false;
      }
    }).map(order => {
      try {
        const orderContent = JSON.parse(order.orderContent);
        return {
          orderId: order.id,
          orderDate: order.createdAt,
          totalAmount: parseFloat(orderContent.total) || 0,
          status: orderContent.status || 'Unknown',
          products: orderContent.line_items || [],
          billing: orderContent.billing || {},
          shipping: orderContent.shipping || {},
          orderContent: orderContent,
          originalOrder: order
        };
      } catch (e) {
        return {
          orderId: order.id,
          orderDate: order.createdAt,
          totalAmount: 0,
          status: 'Unknown',
          products: [],
          billing: {},
          shipping: {},
          orderContent: {},
          originalOrder: order
        };
      }
    });
  }

  private getCustomerRepairs(customerEmail: string): any[] {
    const customerOrderIds = new Set<string>();
    
    // Get all order IDs for this customer from loaded orders
    this.allRawOrders.forEach(order => {
      try {
        const orderContent = JSON.parse(order.orderContent);
        if (orderContent.billing &&
            orderContent.billing.email &&
            orderContent.billing.email.toLowerCase() === customerEmail.toLowerCase()) {
          customerOrderIds.add(order.id);
        }
      } catch (e) {
        // Skip invalid orders
      }
    });

    // Filter schedule requests that match either order ID or email
    return this.allScheduleRequests.filter(repair => {
      // Check if repair's orderId matches any of this customer's orders
      if (repair.orderId && customerOrderIds.has(repair.orderId)) {
        return true;
      }
      
      // Check if repair's contact email matches customer email
      return repair.contactEmail &&
             repair.contactEmail.toLowerCase() === customerEmail.toLowerCase();
    }).map(repair => {
      return {
        repairId: repair.scheduleRequestId,
        scheduleDate: repair.scheduleRequestDate,
        contactName: repair.contactName,
        contactPhone: repair.contactPhone,
        contactEmail: repair.contactEmail,
        propertyAddress: repair.buyerProperty,
        additionalInfo: repair.additionalShare,
        orderId: repair.orderId,
        createdAt: repair.createdAt,
        originalRepair: repair,
        matchedBy: repair.orderId && customerOrderIds.has(repair.orderId) ? 'orderId' : 'email'
      };
    });
  }

  private handleError(error: any) {
    console.error('CRM Error:', error);
    this.hasError = true;
    this.errorMessage = this.getErrorMessage(error);
  }

  private getErrorMessage(error: any): string {
    if (error.message && error.message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    } else if (error.networkError) {
      return 'Network error. Please check your connection.';
    } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      return error.graphQLErrors[0].message;
    } else {
      return 'An unexpected error occurred. Please try again.';
    }
  }

  private highLightFirstRow() {
    if (this.allCustomers.length > 0) {
      this.selectedId = this.allCustomers[0].orderId;
    }
  }

  // Public getters for template
  get isDataAvailable(): boolean {
    return this.customerCount > 0;
  }

  get shouldShowEmptyState(): boolean {
    return !this.isLoading && this.customerCount === 0 && !this.hasError;
  }

  get shouldShowErrorState(): boolean {
    return this.hasError && !this.isLoading;
  }

  get emptyStateMessage(): string {
    if (this.isSearchMode) {
      return `No customers found for "${this.currentSearchTerm}"`;
    }
    return 'No customers found';
  }

  get emptyStateDescription(): string {
    if (this.isSearchMode) {
      return 'Try adjusting your search terms or clear the search to view all customers.';
    }
    return 'No customer data available at the moment.';
  }

  get customerCountText(): string {
    let baseText = this.customerCount + ' customers';
    
    if (this.isSearchMode) {
      baseText += ` found for "${this.currentSearchTerm}"`;
    } else {
      if (this.hasMoreOrderData) {
        baseText += ' loaded';
        if (this.totalOrderCount > 0) {
          const estimatedTotal = Math.ceil(this.customerCount * (this.totalOrderCount / this.allRawOrders.length));
          baseText += ' (est. ' + estimatedTotal + ' total)';
        }
      } else {
        baseText += ' (all loaded)';
      }
    }

    if (this.totalScheduleRequestCount > 0) {
      baseText += ' - ' + this.totalScheduleRequestCount + ' repairs processed';
    }

    return baseText;
  }
}

export interface CustomerData {
  orderId: string;
  customerName: string;
  email: string;
  phone: string;
  address: string;
  createdAt: string;
  orderContent: any;
  originalOrder?: any;
  quotesOrdered: number;
  repairsSubmitted: number;
  marketName: string;
  firmName: string;
}

export interface CustomerStats {
  quotesOrdered: number;
  repairsSubmitted: number;
}


// WEBPACK FOOTER //
// /vercel/path0/src/app/main/content/apps/e-commerce/orders/sidenavs/left/operations/crm-flow/crm-flow.component.ts